import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { Rocket2 } from '@apollo/space-kit/illustrations/Rocket2';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Prompt, Redirect, useLocation } from 'react-router';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import {
  API_SCHEMA_ID,
  SUPERGRAPH_SCHEMA_ID,
} from '../../documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import { GraphRef } from '../../hooks/useGraphRef';
import { proposalEditorRouteConfig } from '../../routes';
import { getConflictMonacoRanges } from '../diffHelpers/generateGitStyleMerge';
import { ImplementedChangesFragment } from '../implementationGutterIndicator/ImplementationGutterIndicator';

import { AddProposalSubgraphModal } from './addProposalSubgraphModal/AddProposalSubgraphModal';
import { DeleteProposalSubgraphModal } from './deleteProposalSubgraph/DeleteProposalSubgraph';
import { DiscardProposalEditorChangesModal } from './DiscardProposalEditorChangesModal';
import {
  ProposalSchemaInfo,
  useProposalEditorState,
} from './hooks/useProposalEditorState/useProposalEditorState';
import { ProposalEditorNav } from './ProposalEditorNav';
import {
  ProposalSDLDisplay,
  SchemaLinterDrawerLinterRuleLevelConfigurationFragment,
} from './proposalSDLDisplay/ProposalSDLDisplay';
import { PublishRevisionModal } from './publishRevisionModal/PublishRevisionModal';
import { RenameProposalSubgraphModal } from './renameProposalSubgraph/RenameProposalSubgraph';
import { DrawerVisibilityState, ProposalBuildInputFragment } from './shared';

const SupergraphAndApiSchemaFragment = gql`
  fragment SupergraphAndApiSchemaFragment on Service {
    id
    variant(name: $graphVariant) {
      id
      proposal {
        id
        status
      }
      activeSchemaPublish @include(if: $shouldFetchActiveSchemaPublish) {
        id
        schema {
          createdAt
          hash
          document
        }
      }
      latestApprovedLaunch {
        id #  the one that might be out of date (distributed systems are hard)
        build {
          id
          result {
            ... on BuildSuccess {
              coreSchema {
                coreHash
                coreDocument
                apiHash
                apiDocument
              }
            }
          }
        }
      }
      latestLaunch {
        id
        build {
          id
          result {
            ... on BuildSuccess {
              coreSchema {
                coreHash
                coreDocument
                apiHash
                apiDocument
              }
            }
            ... on BuildFailure {
              errorMessages {
                code
                locations {
                  column
                  line
                }
                message
                failedStep
              }
            }
          }
        }
      }
    }
  }
`;

function shouldCollapseSidebar(currentVisibleDrawer: DrawerVisibilityState) {
  // If any drawer is open and the user resizes the window to below 1024px
  // we should automatically collapse the schema selection sidebar so
  // the editor remains readable
  if (currentVisibleDrawer !== 'none') {
    return window.innerWidth <= 1024;
  } else {
    return window.innerWidth < 768;
  }
}
export const ProposalEditor = ({
  graphRef,
  mergeBaseCompositionId,
  isStale,
  setInitialLatestRevisionId,
  showPublishRevisionToast,
  subgraphsHaveChanges,
  setSubgraphsHaveChanges,
}: {
  graphRef: GraphRef;
  mergeBaseCompositionId: string | null | undefined;
  isStale: boolean;
  setInitialLatestRevisionId: (id: string) => void;
  showPublishRevisionToast: (
    isSuccessful: boolean,
    errorMessage: string | undefined,
  ) => void;
  subgraphsHaveChanges: boolean;
  setSubgraphsHaveChanges: (hasChanges: boolean) => void;
}) => {
  const subgraphDropdownButtonRefs = useRef<{
    [name: string]: React.RefObject<HTMLAnchorElement>;
  }>({});

  const [saveRevisionDisabled, setSaveRevisionDisabled] = useState(false);
  const { selectedSchema, linkedCoordinateName, pullUpstreamChanges } =
    useRouteParams(proposalEditorRouteConfig);
  const [currentVisibleDrawer, setCurrentVisibleDrawer] =
    useState<DrawerVisibilityState>(linkedCoordinateName ? 'comment' : 'none');
  const [collapsed, setCollapsed] = useState(() =>
    shouldCollapseSidebar(currentVisibleDrawer),
  );
  const isPullUpstreamFlagOn = useLDFlag('nebula_proposals_merge_upstream');
  const shouldPullUpstreamChanges =
    pullUpstreamChanges === 'true' && isPullUpstreamFlagOn;

  const {
    data: { graphPermissions, graphVariantPermissions },
  } = usePermissions(undefined, graphRef?.graphId, graphRef?.graphVariant);
  const canQueryCheckConfiguration =
    graphPermissions?.canQueryCheckConfiguration ?? false;
  const canQueryImplementingServices =
    graphPermissions?.canQueryImplementingServices ?? false;
  const canQueryLaunches = graphVariantPermissions?.canQueryLaunches ?? false;

  const location = useLocation();

  const { data, loading } = useQuery<
    GraphQLTypes.ProposalEditorQuery,
    GraphQLTypes.ProposalEditorQueryVariables
  >(
    gql`
      # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
      query ProposalEditorQuery(
        $graphId: ID!
        $graphVariant: String!
        $canQueryCheckConfiguration: Boolean!
        $pullUpstreamChanges: Boolean!
        $hasMergeBaseCompositionId: Boolean!
        $shouldFetchActiveSchemaPublish: Boolean!
      ) {
        service(id: $graphId) {
          id
          ...SupergraphAndApiSchemaFragment
          name
          linterConfiguration @include(if: $canQueryCheckConfiguration) {
            rules {
              ...SchemaLinterDrawerLinterRuleLevelConfigurationFragment
            }
          }
          variant(name: $graphVariant) {
            id
            firstLaunch: launchHistory(limit: 1, orderBy: CREATED_ASC)
              @skip(if: $hasMergeBaseCompositionId) {
              id
              createdAt
              buildInput {
                ... on CompositionBuildInput {
                  subgraphs {
                    hash
                    name
                  }
                }
              }
            }
            updatedSubgraphsSinceCreation {
              name
              updatedAt
            }
            proposal {
              sourceVariant {
                id
                name
                activeSchemaPublish @include(if: $pullUpstreamChanges) {
                  id
                  compositionResult {
                    graphCompositionID
                  }
                }
              }
              id
              parentComments(filter: { type: [CHANGE] }) {
                ... on ParentChangeProposalComment {
                  id
                  replyCount
                  schemaCoordinate
                  schemaScope
                  createdAt
                  outdated
                }
              }
              latestRevision {
                id
                launch {
                  id # the one that is not out of date
                }
                mergeBaseLaunch @include(if: $hasMergeBaseCompositionId) {
                  id
                  createdAt
                  buildInput {
                    ... on CompositionBuildInput {
                      subgraphs {
                        hash
                        name
                      }
                    }
                  }
                }
              }
              canEditProposal
              ...ImplementedChangesFragment @defer
            }
            latestLaunch {
              id
              buildInput {
                ... on CompositionBuildInput {
                  subgraphs {
                    sdl(graphId: $graphId)
                  }
                }
              }
            }
            ...ProposalBuildInputFragment
          }
        }
      }
      ${ProposalBuildInputFragment}
      ${SupergraphAndApiSchemaFragment}
      ${ImplementedChangesFragment}
      ${SchemaLinterDrawerLinterRuleLevelConfigurationFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        ...graphRef,
        canQueryCheckConfiguration,
        pullUpstreamChanges: shouldPullUpstreamChanges,
        hasMergeBaseCompositionId: !!mergeBaseCompositionId,
        shouldFetchActiveSchemaPublish: !canQueryLaunches,
      },
    },
  );

  const graph = data?.service;
  const variant = graph?.variant;
  const proposal = variant?.proposal;

  const canEditProposal =
    !!proposal?.canEditProposal &&
    proposal?.status !== ProposalStatus.IMPLEMENTED &&
    proposal?.status !== ProposalStatus.CLOSED;

  const latestLaunchId = variant?.latestLaunch?.id;
  const latestLaunchBuildResult = variant?.latestLaunch?.build?.result;
  const buildErrors =
    latestLaunchBuildResult?.__typename === 'BuildFailure'
      ? latestLaunchBuildResult.errorMessages
      : undefined;

  const sourceGraphVariant = variant?.proposal?.sourceVariant.name;
  const sourceCompositionId =
    variant?.proposal?.sourceVariant.activeSchemaPublish?.compositionResult
      ?.graphCompositionID;

  const implementedChangesBySchema: Record<
    string,
    Omit<
      GraphQLTypes.ImplementedChangesFragment['implementedChanges'][0],
      'subgraph'
    >[]
  > = useMemo(
    () => ({
      ..._.groupBy(
        graph?.variant?.proposal?.implementedChanges ?? [],
        'subgraph',
      ),
      [API_SCHEMA_ID]: graph?.variant?.proposal?.implementedChanges ?? [],
      [SUPERGRAPH_SCHEMA_ID]:
        graph?.variant?.proposal?.implementedChanges ?? [],
    }),
    [graph],
  );

  const [
    shouldPollForApiAndSupergraphSchema,
    setShouldPollForApiAndSupergraphSchema,
  ] = useState(true);

  const [
    fetchSupergraphAndApiSchema,
    { loading: supergraphAndApiDataLoading },
  ] = useLazyQuery<
    GraphQLTypes.ProposalEditorSupergraphAndApiSchemaPollQuery,
    GraphQLTypes.ProposalEditorSupergraphAndApiSchemaPollQueryVariables
  >(
    gql`
      query ProposalEditorSupergraphAndApiSchemaPollQuery(
        $graphId: ID!
        $graphVariant: String!
        $shouldFetchActiveSchemaPublish: Boolean!
      ) {
        service(id: $graphId) {
          id
          ...SupergraphAndApiSchemaFragment
        }
      }
      ${SupergraphAndApiSchemaFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        ...graphRef,
        shouldFetchActiveSchemaPublish: !canQueryLaunches,
      },
      pollInterval: shouldPollForApiAndSupergraphSchema ? 5000 : 0,
      // TODO: Confirm this is the correct change for Apollo Client 3.8
      notifyOnNetworkStatusChange: true,
    },
  );

  const latestApprovedLaunchIdAsync = variant?.latestApprovedLaunch?.id;
  const launchIdSync = proposal?.latestRevision.launch?.id;
  const isLaunchFullyPropagated =
    !!latestApprovedLaunchIdAsync &&
    !!launchIdSync &&
    latestApprovedLaunchIdAsync === launchIdSync;

  useEffect(() => {
    if (!isLaunchFullyPropagated) {
      setShouldPollForApiAndSupergraphSchema(true);
    }
  }, [isLaunchFullyPropagated, shouldPollForApiAndSupergraphSchema]);

  // if the build hasn't finished yet, poll for the api & supergraph schemas until it does
  useEffect(() => {
    if (
      !supergraphAndApiDataLoading &&
      !loading &&
      data &&
      (!latestApprovedLaunchIdAsync || !isLaunchFullyPropagated) &&
      (!latestLaunchBuildResult ||
        latestLaunchBuildResult?.__typename === 'BuildSuccess')
    ) {
      fetchSupergraphAndApiSchema();
    }

    if (
      isLaunchFullyPropagated &&
      graph?.variant?.latestApprovedLaunch &&
      latestLaunchBuildResult?.__typename === 'BuildSuccess'
    ) {
      setShouldPollForApiAndSupergraphSchema(false);
    }
  }, [
    data,
    loading,
    variant,
    graph,
    latestLaunchBuildResult,
    supergraphAndApiDataLoading,
    fetchSupergraphAndApiSchema,
    isLaunchFullyPropagated,
    latestApprovedLaunchIdAsync,
  ]);

  const apiSchemaInfo = useMemo((): ProposalSchemaInfo | null => {
    const latestSuccessfulBuildResult =
      variant?.latestApprovedLaunch?.build?.result;

    if (
      (latestSuccessfulBuildResult?.__typename !== 'BuildSuccess' ||
        // TODO: This is a patch that prevents a fatal error when the coreSchema is missing
        // We should remove it once this issue is fixed in the backend
        !latestSuccessfulBuildResult?.coreSchema) &&
      !variant?.activeSchemaPublish
    ) {
      return null;
    }

    const latestApiSdl =
      latestLaunchBuildResult?.__typename === 'BuildSuccess'
        ? latestLaunchBuildResult.coreSchema.apiDocument
        : variant?.activeSchemaPublish?.schema.document;

    const latestSuccessfulApiSdl =
      latestSuccessfulBuildResult?.__typename === 'BuildSuccess'
        ? latestSuccessfulBuildResult.coreSchema.apiDocument
        : undefined;

    // latestSdl is undefined if composition failed
    // we still want to show stale api sdl, which we get from latestApprovedLaunch
    const apiSdl = latestApiSdl ?? latestSuccessfulApiSdl;

    const apiHash =
      latestSuccessfulBuildResult?.__typename === 'BuildSuccess'
        ? latestSuccessfulBuildResult.coreSchema.apiHash
        : variant?.activeSchemaPublish?.schema.hash;

    if (!apiSdl || !apiHash) {
      return null;
    }

    return {
      type: 'Graph',
      displayName: 'API',
      id: API_SCHEMA_ID,
      text: apiSdl,
      sdlIsLoading: false,
      initialText: apiSdl,
      hash: apiHash,
      language: 'graphql',
      isNewSubgraphFromLatestSourceVariant: false,
    };
  }, [
    latestLaunchBuildResult,
    variant?.activeSchemaPublish,
    variant?.latestApprovedLaunch?.build?.result,
  ]);

  const supergraphInfo = useMemo((): ProposalSchemaInfo | null => {
    const latestSupergraphSdl =
      latestLaunchBuildResult?.__typename === 'BuildSuccess'
        ? latestLaunchBuildResult.coreSchema.coreDocument
        : undefined;

    const latestSuccessfulBuildResult =
      variant?.latestApprovedLaunch?.build?.result;

    if (
      (!latestSupergraphSdl && !latestSuccessfulBuildResult) ||
      latestSuccessfulBuildResult?.__typename !== 'BuildSuccess' ||
      // TODO: This is a patch that prevents a fatal error when the coreSchema is missing
      // We should remove it once this issue is fixed in the backend
      !latestSuccessfulBuildResult?.coreSchema
    ) {
      return null;
    }

    const latestSuccessfulSupergraphSdl =
      latestSuccessfulBuildResult.coreSchema.coreDocument;

    // latestSdl is undefined if composition failed
    // we still want to show stale supergraph sdl, which we get from latestApprovedLaunch
    const supergraphSdl = latestSupergraphSdl ?? latestSuccessfulSupergraphSdl;

    return {
      type: 'Graph',
      displayName: 'Supergraph',
      id: SUPERGRAPH_SCHEMA_ID,
      text: supergraphSdl,
      sdlIsLoading: false,
      initialText: supergraphSdl,
      hash: latestSuccessfulBuildResult.coreSchema.coreHash,
      language: 'graphql',
      isNewSubgraphFromLatestSourceVariant: false,
    };
  }, [latestLaunchBuildResult, variant?.latestApprovedLaunch]);

  const subgraphsFromBuildInput =
    variant?.latestLaunch?.buildInput.__typename === 'CompositionBuildInput'
      ? variant.latestLaunch.buildInput.subgraphs
      : undefined;

  const {
    editor,
    modelsByName,
    languageServerDiagnosticsBySubgraphName,
    graphInfoMap,
    selectedSchemaInfo,
    changedSubgraphs,
    subgraphsWithMetadata,
    setSubgraphsWithMetadata,
    hasSubgraphBeenSaved,
    updateChangedSubgraphsTextPostPublish,
    storeSchemaChanges,
    discardUnsavedSchemaChanges,
    onAddSubgraph,
    onRenameSubgraph,
    setSelectedSchemaName,
  } = useProposalEditorState({
    graphRef,
    apiSchemaInfo,
    supergraphInfo,
    canEditProposal,
    mergeBaseOrFirstLaunch:
      variant?.proposal?.latestRevision?.mergeBaseLaunch ??
      graph?.variant?.firstLaunch?.[0],
    subgraphs: subgraphsFromBuildInput,
    shouldPullUpstreamChanges,
    mergeBaseCompositionId,
    sourceGraphVariant,
  });

  useEffect(() => {
    if (
      shouldPullUpstreamChanges &&
      selectedSchemaInfo &&
      changedSubgraphs?.[selectedSchemaInfo.id]?.hasConflicts
    ) {
      // TODO: We should expand upon this solution to allow the user to navigate between the ranges calculated here
      const conflictRanges = getConflictMonacoRanges(selectedSchemaInfo.text);

      if (isMinLengthArray(1, conflictRanges)) {
        editor.revealLineInCenterIfOutsideViewport(
          conflictRanges[0].startLineNumber,
        );
      }
    }
  }, [changedSubgraphs, editor, selectedSchemaInfo, shouldPullUpstreamChanges]);

  const subgraphHashByName = useMemo(() => {
    return subgraphsWithMetadata.map(([subgraphName, value]) => ({
      hash: value.hash,
      sdl: () => modelsByName[subgraphName]?.getValue(),
      name: subgraphName,
    }));
  }, [modelsByName, subgraphsWithMetadata]);

  useEffect(() => {
    const hasChanges = Object.values(changedSubgraphs)?.some(
      (subgraph) => subgraph.changed,
    );
    setSubgraphsHaveChanges(hasChanges);
  }, [changedSubgraphs, setSubgraphsHaveChanges]);

  const selectedSubgraphHasChanges = useMemo(() => {
    return selectedSchema ? !!changedSubgraphs[selectedSchema]?.changed : false;
  }, [changedSubgraphs, selectedSchema]);

  useEffect(() => {
    const exitPromptEventListener = (event: BeforeUnloadEvent) => {
      if (subgraphsHaveChanges) {
        // WebKit-derived browsers don't follow the spec for the dialog box.
        // https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent#examples
        const confirmationMessage = `\o/`;

        // eslint-disable-next-line no-param-reassign
        (event || window.event).returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };

    // Listening for window unload, aka window refreshed or closed
    window.addEventListener('beforeunload', exitPromptEventListener);

    // Removing listener when component unmounts, aka in useEffect callback
    return () => {
      window.removeEventListener('beforeunload', exitPromptEventListener);
    };
  }, [setSubgraphsHaveChanges, subgraphsHaveChanges]);

  // reset subgraphsHaveChanges when unmounting
  useEffect(() => {
    return () => {
      setSubgraphsHaveChanges(false);
    };
  }, [setSubgraphsHaveChanges]);

  useEffect(() => {
    const resizeEventListener = () => {
      const shouldCollapse = shouldCollapseSidebar(currentVisibleDrawer);
      if (shouldCollapse) setCollapsed(true);
    };

    window.addEventListener('resize', resizeEventListener);
    return () => {
      window.removeEventListener('resize', resizeEventListener);
    };
  }, [currentVisibleDrawer]);

  const changedSubgraphsWithMetadata = useMemo(
    () =>
      subgraphsWithMetadata.filter(
        ([subgraphName]) => changedSubgraphs[subgraphName]?.changed,
      ),
    [changedSubgraphs, subgraphsWithMetadata],
  );

  const changedSubgraphsWithSyntaxErrors = useMemo(() => {
    return Object.keys(changedSubgraphs)
      .map(
        (key) =>
          [key, languageServerDiagnosticsBySubgraphName[key]?.syntax] as const,
      )
      .filter(
        (entry): entry is [(typeof entry)[0], NonNullable<(typeof entry)[1]>] =>
          isMinLengthArray(1, entry[1]),
      );
  }, [changedSubgraphs, languageServerDiagnosticsBySubgraphName]);

  if (
    loading ||
    (canQueryImplementingServices &&
      !isMinLengthArray(1, subgraphsWithMetadata))
  ) {
    return <Loading />;
  } else if (!variant?.proposal || !graph) {
    return <NotFound />;
  }
  const proposalId = variant.proposal.id;

  if (
    !selectedSchema ||
    !selectedSchemaInfo || // this is just here for ts to know its not undefined below
    Object.keys(graphInfoMap).every(
      (subgraphName) => subgraphName !== selectedSchema,
    )
  ) {
    return (
      <Redirect
        to={proposalEditorRouteConfig.locationFrom({
          location,
          patch: {
            selectedSchema:
              _.maxBy(
                // if shouldPullUpstream, possibly select a deleted subgraph otherwise filter deleted subgraphs out
                shouldPullUpstreamChanges
                  ? variant.updatedSubgraphsSinceCreation
                  : variant.updatedSubgraphsSinceCreation?.filter((subgraph) =>
                      subgraphsWithMetadata.some(
                        (subgraphData) => subgraphData[0] === subgraph.name,
                      ),
                    ),
                (subgraph) => moment(subgraph.updatedAt).valueOf(),
              )?.name ??
              subgraphsWithMetadata[0]?.[0] ??
              API_SCHEMA_ID,
          },
        })}
      />
    );
  }

  const parentComments = variant.proposal.parentComments.filter(
    (
      comment,
    ): comment is GraphQLTypes.ProposalEditorQuery_service_variant_proposal_parentComments_ParentChangeProposalComment =>
      comment.__typename === 'ParentChangeProposalComment',
  );

  // If a user opens up a coordinate without a comment remove it from the url
  if (
    linkedCoordinateName &&
    !parentComments.some(
      (comment) =>
        comment.schemaScope === selectedSchemaInfo?.id &&
        comment.schemaCoordinate === linkedCoordinateName,
    )
  ) {
    return (
      <Redirect
        to={proposalEditorRouteConfig.locationFrom({
          location,
          patch: {
            linkedCoordinateName: RouteConfig.DELETE,
          },
        })}
      />
    );
  }
  return (
    <>
      <div className="flex w-full flex-1 overflow-hidden">
        <Prompt
          message={(redirectLocation) => {
            if (redirectLocation.pathname !== location.pathname) {
              return 'Are you sure you want to leave this page? Your progress will not be saved.';
            }
            return true;
          }}
          when={subgraphsHaveChanges && !saveRevisionDisabled}
        />
        <ProposalEditorNav
          graphRef={graphRef}
          buildErrors={buildErrors}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          changedSubgraphs={changedSubgraphs}
          changedSubgraphsWithSyntaxErrors={changedSubgraphsWithSyntaxErrors}
          canEditProposal={canEditProposal}
          selectedSchema={selectedSchema}
          subgraphDropdownButtonRefs={subgraphDropdownButtonRefs}
          isStale={isStale}
          subgraphsHaveChanges={subgraphsHaveChanges}
          graph={graph}
          graphInfoMap={graphInfoMap}
          onSwitchEditor={storeSchemaChanges(selectedSchemaInfo)}
          subgraphsWithMetadata={subgraphsWithMetadata}
          saveRevisionDisabled={saveRevisionDisabled}
          subgraphHasBeenSaved={hasSubgraphBeenSaved}
          languageServerDiagnosticsBySubgraphName={
            languageServerDiagnosticsBySubgraphName
          }
          isPullUpstream={shouldPullUpstreamChanges}
        />
        <div className="flex w-0 flex-1 flex-col">
          {selectedSchema ? (
            <ProposalSDLDisplay
              className="flex size-full flex-col"
              editor={editor}
              selectedSchemaInfo={selectedSchemaInfo}
              graphRef={graphRef}
              isStale={isStale}
              buildErrors={buildErrors}
              onSaveRevision={storeSchemaChanges(selectedSchemaInfo)}
              subgraphsHaveChanges={subgraphsHaveChanges}
              hasSubgraphs={isMinLengthArray(1, subgraphsWithMetadata)}
              collapsed={collapsed}
              sdlIsLoading={selectedSchemaInfo.sdlIsLoading}
              dataIsNotConsistent={!isLaunchFullyPropagated}
              changedSubgraphs={changedSubgraphs}
              linterRules={graph.linterConfiguration?.rules}
              saveRevisionDisabled={saveRevisionDisabled}
              canEditProposal={canEditProposal}
              parentComments={parentComments}
              canQueryLaunches={canQueryLaunches}
              implementedChanges={
                implementedChangesBySchema[selectedSchema] ?? []
              }
              sourceGraphVariant={sourceGraphVariant}
              currentVisibleDrawer={currentVisibleDrawer}
              setCurrentVisibleDrawer={setCurrentVisibleDrawer}
              selectedSubgraphHasChanges={selectedSubgraphHasChanges}
              proposedSubgraphs={subgraphHashByName}
              proposedSupergraphSchema={{
                hash: supergraphInfo?.hash ?? null,
                getSdl: () => supergraphInfo?.text,
              }}
            />
          ) : (
            <div className="flex flex-1 overflow-y-auto">
              <div className="mt-32 flex flex-1 flex-col items-center">
                <Rocket2 />
                <div className="mt-3 font-semibold text-placeholder">
                  {!supergraphInfo
                    ? 'Graph has no active publish.'
                    : `Subgraph${
                        selectedSchema && ` ${selectedSchema}`
                      } not recognized, please select a
              subgraph on the left.`}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <LazyUrlModal name={Config.modals.addProposalSubgraph}>
        {({ closeModal, isOpen }) => (
          <AddProposalSubgraphModal
            isOpen={isOpen}
            onClose={closeModal}
            onAddSubgraph={onAddSubgraph}
            existingSubgraphNames={subgraphsWithMetadata.map(
              ([, subgraph]) => subgraph.displayName,
            )}
          />
        )}
      </LazyUrlModal>
      {latestLaunchId && (
        <>
          <LazyUrlModal name={Config.modals.publishRevision}>
            {({ closeModal, isOpen }) => (
              <PublishRevisionModal
                proposalId={proposalId}
                isOpen={isOpen}
                onClose={closeModal}
                changedSubgraphsWithMetadata={changedSubgraphsWithMetadata}
                graphRef={graphRef}
                setInitialLatestRevisionId={setInitialLatestRevisionId}
                isStale={isStale}
                latestLaunchId={latestLaunchId}
                setSaveRevisionDisabled={setSaveRevisionDisabled}
                showPublishRevisionToast={showPublishRevisionToast}
                updateChangedSubgraphsTextPostPublish={
                  updateChangedSubgraphsTextPostPublish
                }
                subgraphsHaveChanges={subgraphsHaveChanges}
                changedSubgraphsWithSyntaxErrors={
                  changedSubgraphsWithSyntaxErrors
                }
                canEditProposal={canEditProposal}
                isMergeRevision={shouldPullUpstreamChanges}
                sourceGraphVariant={sourceGraphVariant}
                sourceCompositionId={sourceCompositionId}
              />
            )}
          </LazyUrlModal>
          <LazyUrlModal name={Config.modals.deleteProposalSubgraph}>
            {({ closeModal, isOpen }) => (
              <DeleteProposalSubgraphModal
                isOpen={isOpen}
                onClose={closeModal}
                selectedSchemaInfo={selectedSchemaInfo}
                finalFocusRef={
                  subgraphDropdownButtonRefs.current[selectedSchema]
                }
                setSubgraphsWithMetadata={setSubgraphsWithMetadata}
                setInitialLatestRevisionId={setInitialLatestRevisionId}
                isStale={isStale}
                latestLaunchId={latestLaunchId}
              />
            )}
          </LazyUrlModal>
        </>
      )}
      <LazyUrlModal name={Config.modals.renameProposalSubgraph}>
        {({ closeModal, isOpen }) => (
          <RenameProposalSubgraphModal
            isOpen={isOpen}
            onClose={closeModal}
            finalFocusRef={subgraphDropdownButtonRefs.current[selectedSchema]}
            selectedSchemaInfo={selectedSchemaInfo}
            setSubgraphsWithMetadata={setSubgraphsWithMetadata}
            onRenameSubgraph={onRenameSubgraph}
            setSelectedSchemaName={setSelectedSchemaName}
            existingSubgraphNames={subgraphsWithMetadata.map(
              ([, subgraph]) => subgraph.displayName,
            )}
          />
        )}
      </LazyUrlModal>
      <LazyUrlModal name={Config.modals.discardChanges}>
        {({ closeModal, isOpen }) => (
          <DiscardProposalEditorChangesModal
            isOpen={isOpen}
            onClose={closeModal}
            discardUnsavedSubgraphChanges={discardUnsavedSchemaChanges}
          />
        )}
      </LazyUrlModal>
    </>
  );
};
